import React, { useState } from 'react';
import { X, Share, PlusSquare, Smartphone, Check, ArrowRight } from 'lucide-react';
import { usePwaInstall, PWA_INSTALL_SUSPENDED } from '../../lib/pwa-install';
import { useDialogA11y } from '../../lib/useDialogA11y';

export interface MobilePwaInstallSheetProps {
  isOpen: boolean;
  onClose: () => void;
  notify?: (msg: string) => void;
}

export const MobilePwaInstallSheet: React.FC<MobilePwaInstallSheetProps> = ({
  isOpen,
  onClose,
  notify,
}) => {
  const { isInstalled, isIos, canPromptDirectly, promptInstall } = usePwaInstall();
  const [installing, setInstalling] = useState(false);

  const { ref: dialogRef } = useDialogA11y({ open: isOpen && !PWA_INSTALL_SUSPENDED, onClose });

  if (!isOpen || PWA_INSTALL_SUSPENDED) return null;

  const handleInstallClick = async () => {
    setInstalling(true);
    try {
      const outcome = await promptInstall();
      if (outcome === 'accepted') {
        notify?.('Plotwise installeres på din enhed!');
        onClose();
      } else if (outcome === 'dismissed') {
        notify?.('Installation annulleret.');
      }
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div
      ref={dialogRef}
      className="mobile-inspector-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Installer Plotwise som app"
      onClick={onClose}
      style={{
        zIndex: 'calc(var(--z-modal) + 70)',
        display: 'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
      }}
    >
      <div
        className="mobile-inspector-sheet"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 500,
          background: 'linear-gradient(180deg, #1b3823 0%, #102116 100%)',
          borderTop: '1px solid rgba(74, 222, 128, 0.3)',
          borderRadius: '20px 20px 0 0',
          padding: '20px 18px max(24px, env(safe-area-inset-bottom, 24px))',
          color: '#ffffff',
          boxShadow: '0 -10px 40px rgba(0,0,0,0.6)',
          animation: 'slideUpMobileSheet 0.24s cubic-bezier(0.16, 1, 0.3, 1)',
        }}
      >
        <div className="mobile-inspector-handle" />

        {/* Top Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              style={{
                width: 44,
                height: 44,
                borderRadius: 12,
                background: 'linear-gradient(135deg, #16a34a 0%, #0d5426 100%)',
                border: '1.5px solid rgba(74, 222, 128, 0.4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
                boxShadow: '0 4px 14px rgba(22, 163, 74, 0.35)',
              }}
            >
              <Smartphone size={22} />
            </div>
            <div>
              <h2
                style={{
                  margin: 0,
                  fontSize: '1.1rem',
                  fontWeight: 800,
                  color: '#ffffff',
                  letterSpacing: '-0.01em',
                }}
              >
                Føj Plotwise til hjemmeskærm
              </h2>
              <p style={{ margin: '2px 0 0', fontSize: '0.78rem', color: '#cbd5e1' }}>
                Brug Plotwise i fuld skærm direkte som en app
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.08)',
              border: '1px solid rgba(255, 255, 255, 0.12)',
              borderRadius: 10,
              width: 36,
              height: 36,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#cbd5e1',
              cursor: 'pointer',
            }}
            aria-label="Luk"
          >
            <X size={18} />
          </button>
        </div>

        {/* Benefits Pill List */}
        <div
          style={{
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: 12,
            padding: '12px 14px',
            marginBottom: 18,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              fontSize: '0.82rem',
              color: '#e2e8f0',
            }}
          >
            <span style={{ color: '#4ade80', display: 'flex' }}>
              <Check size={16} />
            </span>
            <span>100% fuldskærm uden browserbjælker og adressefelter</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              fontSize: '0.82rem',
              color: '#e2e8f0',
            }}
          >
            <span style={{ color: '#4ade80', display: 'flex' }}>
              <Check size={16} />
            </span>
            <span>Hurtig opstart direkte fra dit skrivebord eller telefon</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 9,
              fontSize: '0.82rem',
              color: '#e2e8f0',
            }}
          >
            <span style={{ color: '#4ade80', display: 'flex' }}>
              <Check size={16} />
            </span>
            <span>Automatisk synkroniseret med dine vildtagre og revirer</span>
          </div>
        </div>

        {/* Instructions depending on platform */}
        {isInstalled ? (
          <div
            style={{
              padding: '16px',
              borderRadius: 12,
              background: 'rgba(34, 197, 94, 0.15)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              textAlign: 'center',
            }}
          >
            <p style={{ margin: 0, fontSize: '0.9rem', fontWeight: 700, color: '#4ade80' }}>
              ✓ Plotwise kører allerede som installeret app!
            </p>
          </div>
        ) : isIos ? (
          /* iOS Safari Guide */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                background: 'rgba(255, 255, 255, 0.06)',
                borderRadius: 10,
                padding: '12px 14px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  background: 'rgba(56, 189, 248, 0.15)',
                  border: '1px solid rgba(56, 189, 248, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#38bdf8',
                  flexShrink: 0,
                }}
              >
                <Share size={18} />
              </div>
              <div>
                <strong style={{ display: 'block', fontSize: '0.86rem', color: '#ffffff' }}>
                  Trin 1: Tryk på Del i Safari
                </strong>
                <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                  Ikonet med firkant og pil op [ ⎋ ] i Safaris bundmenu
                </span>
              </div>
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                background: 'rgba(255, 255, 255, 0.06)',
                borderRadius: 10,
                padding: '12px 14px',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  background: 'rgba(34, 197, 94, 0.15)',
                  border: '1px solid rgba(34, 197, 94, 0.3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#4ade80',
                  flexShrink: 0,
                }}
              >
                <PlusSquare size={18} />
              </div>
              <div>
                <strong style={{ display: 'block', fontSize: '0.86rem', color: '#ffffff' }}>
                  Trin 2: Vælg &quot;Føj til hjemmeskærm&quot;
                </strong>
                <span style={{ fontSize: '0.74rem', color: '#94a3b8' }}>
                  Rul ned i menuen og tryk på &quot;Føj til hjemmeskærm&quot; og derefter
                  &quot;Tilføj&quot;
                </span>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              style={{
                width: '100%',
                minHeight: 48,
                marginTop: 6,
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                border: 'none',
                borderRadius: 12,
                color: '#ffffff',
                fontSize: '0.92rem',
                fontWeight: 750,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                boxShadow: '0 4px 16px rgba(22, 163, 74, 0.4)',
              }}
            >
              <span>Forstået</span>
            </button>
          </div>
        ) : (
          /* Android / Desktop Chrome Direct Button */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {canPromptDirectly ? (
              <button
                type="button"
                disabled={installing}
                onClick={handleInstallClick}
                style={{
                  width: '100%',
                  minHeight: 50,
                  background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                  border: 'none',
                  borderRadius: 12,
                  color: '#ffffff',
                  fontSize: '0.94rem',
                  fontWeight: 750,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 8,
                  boxShadow: '0 4px 18px rgba(22, 163, 74, 0.4)',
                }}
              >
                <Smartphone size={18} />
                <span>{installing ? 'Installerer…' : 'Installer app nu'}</span>
                <ArrowRight size={16} />
              </button>
            ) : (
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: 10,
                  padding: '12px 14px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  fontSize: '0.82rem',
                  color: '#cbd5e1',
                  lineHeight: 1.4,
                }}
              >
                Åbn browserens menu (tre prikker <strong>⋮</strong>) øverst til højre og vælg{' '}
                <strong style={{ color: '#4ade80' }}>&quot;Installer app&quot;</strong> eller{' '}
                <strong style={{ color: '#4ade80' }}>&quot;Føj til startskærm&quot;</strong>.
              </div>
            )}
            <button
              type="button"
              onClick={onClose}
              style={{
                width: '100%',
                minHeight: 44,
                background: 'transparent',
                border: '1px solid rgba(255, 255, 255, 0.14)',
                borderRadius: 10,
                color: '#cbd5e1',
                fontSize: '0.86rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Ikke nu
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
